import { useCallback, useEffect, useState } from "react";
import {
  User, UserCircle2, Baby, UserPlus, CheckCircle2, Sparkles,
  FileText, ChevronRight, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import PersonUploader from "@/components/PersonUploader";
import SmartSort from "@/components/SmartSort";
import AddFamilyMemberModal, { FamilyMemberGuidanceModal } from "@/components/AddFamilyMemberModal";

/* Roster Wizard (Phase A)
 * -----------------------
 * Per-person document intake flow: one card per person on the case
 * (Petitioner, Beneficiary, Children, optional Parents). Uploading a
 * document scopes OCR to that specific person via `person_ref`.
 */

const GROUP_LABELS = {
  core: "Petitioner & Principal Applicant",
  children: "Family members (spouse & children derivatives)",
  parents: "Parents",
};

const ROLE_ICONS = {
  petitioner: UserCircle2,
  beneficiary: User,
};

function iconForRow(row) {
  if (ROLE_ICONS[row.person_ref]) return ROLE_ICONS[row.person_ref];
  if (row.person_ref.startsWith("child:")) return Baby;
  return User;
}

export default function RosterStage({ caseId, caseData, onCaseChange }) {
  const [roster, setRoster] = useState(null);
  const [openRef, setOpenRef] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [guidanceUid, setGuidanceUid] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/cases/${caseId}/roster`);
    setRoster(data.roster || []);
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddParent = async (personRef) => {
    // Seed a stub so the parent row appears in the roster manifest
    const stubKey = `${personRef}_first_name`;
    if ((caseData.data || {})[stubKey]) return;
    await api.patch(`/cases/${caseId}`, { section_data: { [stubKey]: "" } });
    // The manifest only surfaces parents with at least one field set; write a
    // space to force visibility, then user can overwrite. Simpler: write a name.
    await api.patch(`/cases/${caseId}`, {
      section_data: { [stubKey]: personRef.replace("_", " ") },
    });
    await onCaseChange?.();
    await load();
    setOpenRef(personRef);
  };

  const grouped = groupRoster(roster || []);

  return (
    <div className="space-y-8" data-testid="roster-stage">
      <IntroCard />

      <SmartSort
        caseId={caseId}
        caseData={caseData}
        roster={roster || []}
        onDone={async () => {
          await onCaseChange?.();
          await load();
        }}
      />

      {grouped.map(({ group, rows }) => (
        <div key={group} className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-secondary)" }}>
              {GROUP_LABELS[group] || group}
            </div>
            {group === "children" && (
              <button
                data-testid="roster-add-member-btn"
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: "var(--action)" }}
                onClick={() => setShowAddMember(true)}
              >
                <UserPlus className="w-3.5 h-3.5" /> Add family member
              </button>
            )}
          </div>

          {rows.length === 0 && group === "children" && (
            <div className="card-surface p-4 text-xs" style={{ color: "var(--text-secondary)" }}>
              No family members added yet. New baby, new spouse, or someone left off the
              petition? Use “Add family member” above — we'll check eligibility and list
              exactly which documents they need.
            </div>
          )}

          {rows.map((row) => (
            <PersonCard
              key={row.person_ref}
              row={row}
              caseId={caseId}
              caseData={caseData}
              open={openRef === row.person_ref}
              onToggle={() => setOpenRef((cur) => (cur === row.person_ref ? null : row.person_ref))}
              onDone={async () => {
                await onCaseChange?.();
                await load();
              }}
              onShowGuidance={row.after_acquired && row.member_uid ? () => setGuidanceUid(row.member_uid) : undefined}
            />
          ))}
        </div>
      ))}

      {showAddMember && (
        <AddFamilyMemberModal
          caseId={caseId}
          caseData={caseData}
          onClose={() => setShowAddMember(false)}
          onAdded={async (personRef) => {
            await onCaseChange?.();
            await load();
            setOpenRef(personRef);
          }}
        />
      )}
      {guidanceUid && (
        <FamilyMemberGuidanceModal
          caseId={caseId}
          uid={guidanceUid}
          onClose={() => setGuidanceUid(null)}
        />
      )}

      <ParentQuickAdd
        alreadyShown={new Set((roster || []).map((r) => r.person_ref))}
        onAdd={handleAddParent}
      />

      <NextStepCard roster={roster || []} />
    </div>
  );
}

function IntroCard() {
  return (
    <div className="card-surface p-6" data-testid="roster-intro">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
          <Sparkles className="w-5 h-5" style={{ color: "var(--ai-text)" }} />
        </div>
        <div>
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Upload documents — one person at a time
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Skip typing. Upload each person's documents below — we'll read, translate,
            and fill the correct case fields. You can always tweak values after.
          </p>
        </div>
      </div>
    </div>
  );
}

function groupRoster(roster) {
  const order = ["core", "children", "parents"];
  const byGroup = {};
  for (const r of roster) {
    const g = r.group || "core";
    (byGroup[g] ||= []).push(r);
  }
  return order
    .filter((g) => byGroup[g] || g === "children")
    .map((g) => ({ group: g, rows: byGroup[g] || [] }));
}

/* -------------- PersonCard -------------- */

function PersonCard({ row, caseId, caseData, open, onToggle, onDone, onShowGuidance }) {
  const Icon = iconForRow(row);
  const done = row.is_complete;
  const uploadedCount = row.docs_uploaded.length;
  const expectedCount = row.docs_expected.filter((d) => !d.optional).length;

  return (
    <div
      className="card-surface anim-in"
      data-testid={`person-card-${row.person_ref}`}
      style={{ borderColor: open ? "var(--action)" : "var(--border-stone)" }}
    >
      <button
        className="w-full flex items-center gap-4 p-5 text-left"
        onClick={onToggle}
        data-testid={`person-toggle-${row.person_ref}`}
      >
        <div
          className="p-3 rounded-xl"
          style={{ background: done ? "#ECFDF5" : "var(--ai-bg)" }}
        >
          <Icon className="w-5 h-5" style={{ color: done ? "#065F46" : "var(--ai-text)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base leading-tight" style={{ color: "var(--text-primary)" }}>
            {row.display_name}
          </div>
          <div className="text-xs mt-0.5 capitalize" style={{ color: "var(--text-secondary)" }}>
            {row.role}
          </div>
          {row.after_acquired && row.nvc_status !== "confirmed" && (
            <span
              className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }}
              data-testid={`nvc-status-pill-${row.person_ref}`}
            >
              Not on NVC record yet
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {done ? (
            <span className="pill pill-ok text-[11px]">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          ) : (
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {uploadedCount}/{expectedCount || row.docs_expected.length} docs
            </span>
          )}
          {open ? (
            <ChevronDown className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          ) : (
            <ChevronRight className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          )}
        </div>
      </button>

      {open && (
        <div className="border-t px-5 py-5 space-y-4" style={{ borderColor: "var(--border-stone)" }}>
          {onShowGuidance && (
            <button
              type="button"
              className="text-xs font-semibold flex items-center gap-1.5"
              style={{ color: "var(--action)" }}
              onClick={onShowGuidance}
              data-testid={`member-guidance-btn-${row.person_ref}`}
            >
              <Sparkles className="w-3.5 h-3.5" /> What {row.display_name} needs — eligibility & NVC steps
            </button>
          )}
          <DocChecklist row={row} caseId={caseId} caseData={caseData} onDone={onDone} />
        </div>
      )}
    </div>
  );
}

/* -------------- DocChecklist -------------- */

function DocChecklist({ row, caseId, caseData, onDone }) {
  const uploaded = row.docs_uploaded;
  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {row.docs_expected.map((d) => {
          const match = uploaded.find((u) => u.doc_type === d.id);
          return (
            <li
              key={d.id}
              className="flex items-center gap-3 text-sm"
              data-testid={`doc-slot-${row.person_ref}-${d.id}`}
            >
              {match ? (
                <CheckCircle2 className="w-4 h-4" style={{ color: "#065F46" }} />
              ) : (
                <span
                  className="w-4 h-4 rounded-full border"
                  style={{ borderColor: "var(--border-stone)" }}
                />
              )}
              <span className="flex-1" style={{ color: "var(--text-primary)" }}>
                {d.label}
              </span>
              {d.optional && (
                <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                  optional
                </span>
              )}
              {match && (
                <span className="pill pill-ai text-[10px]">{match.filename}</span>
              )}
            </li>
          );
        })}
      </ul>
      <PersonUploader row={row} caseId={caseId} caseData={caseData} onDone={onDone} />
      {uploaded.length > 0 && (
        <details className="text-xs" style={{ color: "var(--text-secondary)" }}>
          <summary className="cursor-pointer">
            {uploaded.length} document{uploaded.length === 1 ? "" : "s"} uploaded for {row.display_name}
          </summary>
          <ul className="mt-2 space-y-1">
            {uploaded.map((u) => (
              <li key={u.id} className="flex items-center gap-2" data-testid={`uploaded-doc-${u.id}`}>
                <FileText className="w-3.5 h-3.5" />
                <span className="flex-1 truncate">{u.filename}</span>
                {u.confirmed && <span className="pill pill-ok text-[10px]">Applied</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* -------------- Parent Quick Add -------------- */

function ParentQuickAdd({ alreadyShown, onAdd }) {
  const options = [
    { ref: "petitioner_father", label: "Petitioner's father" },
    { ref: "petitioner_mother", label: "Petitioner's mother" },
    { ref: "beneficiary_father", label: "Beneficiary's father" },
    { ref: "beneficiary_mother", label: "Beneficiary's mother" },
  ].filter((o) => !alreadyShown.has(o.ref));

  if (options.length === 0) return null;

  return (
    <div className="card-surface p-4" data-testid="roster-parents-quickadd">
      <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: "var(--text-secondary)" }}>
        Optional — add a parent to upload their documents
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.ref}
            data-testid={`roster-add-parent-${o.ref}`}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors"
            style={{ borderColor: "var(--border-stone)", color: "var(--text-primary)" }}
            onClick={() => onAdd(o.ref)}
          >
            <UserPlus className="w-3 h-3 inline mr-1" /> {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------- Next Step CTA -------------- */

function NextStepCard({ roster }) {
  const missing = roster.filter((r) => r.group === "core" && !r.is_complete);
  if (missing.length === 0 && roster.length > 0) {
    return (
      <div className="card-surface p-6 text-center anim-in" data-testid="roster-next-step">
        <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: "#065F46" }} />
        <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
          Core roster complete
        </h3>
        <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
          Head to the Review stage to check the extracted data and generate the filing packet.
        </p>
      </div>
    );
  }
  return (
    <div className="card-surface p-5 text-xs" style={{ color: "var(--text-secondary)" }} data-testid="roster-next-step-hint">
      Next: upload documents for {missing.map((m) => m.display_name).join(", ") || "each person"}.
    </div>
  );
}
